import {
  Box,
  Button,
  ButtonProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';

import { Form } from '@/components/Layout/Layout.styled';
import { Page } from '@/components/Layout/Page/Page';

import { getAssetPath } from '@/constants/configs';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';

const FALLBACK_TRANSLATION_PREFIX = 'thirdPartyComplianceCheckFailed';

const LogoFinished = styled(props => (
  <img
    src={getAssetPath('/logo-with-text-white.svg')}
    alt="Logo Finished"
    {...props}
  />
))(({ theme }) => ({
  marginBottom: theme.spacing(46),
  width: 248,
}));

type FailedPageProps = Readonly<{
  translationPrefix: string;
  onAction: () => void;
  testIdPrefix: string;
  onMount?: () => void;
  showTitle?: boolean;
  bodyProps?: TypographyProps;
  buttonProps?: ButtonProps;
}>;

const getTranslationKey = (prefix: string, key: string) =>
  `createNewAccount.${prefix}.${key}`;

export function FailedPage({
  translationPrefix,
  onAction,
  testIdPrefix,
  onMount,
  showTitle = true,
  bodyProps,
  buttonProps,
}: FailedPageProps) {
  const { t } = useTranslate();
  const { isMobile } = useResponsive();

  useEffect(() => {
    onMount?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      paperProps={{
        position: 'right',
        size: 'big',
        hasLogo: false,
        color: theme => theme.palette.overlay.forest.start,
        secondColor: theme => theme.palette.overlay.forest.end,
        children: <LogoFinished />,
      }}
      hasLogo={isMobile}
      formContainerProps={{
        justifyContent: isMobile ? 'space-between' : 'flex-start',
      }}
    >
      <Box
        alignItems={isMobile ? 'center' : 'flex-start'}
        display="flex"
        flexDirection="column"
        mt={isMobile ? 0 : 20}
      >
        {showTitle && (
          <Typography
            variant="headlineH2"
            {...getDataTestId(`${testIdPrefix}-title`)}
          >
            {t(getTranslationKey(translationPrefix, 'header'), {
              defaultValue: t(
                getTranslationKey(FALLBACK_TRANSLATION_PREFIX, 'header'),
              ),
            })}
          </Typography>
        )}
        <Typography
          variant="bodyB2Regular"
          mt={1}
          {...bodyProps}
          {...getDataTestId(`${testIdPrefix}-body`)}
        >
          {t(getTranslationKey(translationPrefix, 'body'))}
        </Typography>
      </Box>
      <Form>
        <Button
          onClick={onAction}
          {...buttonProps}
          {...getDataTestId(`${testIdPrefix}-login-button`)}
        >
          {t(getTranslationKey(translationPrefix, 'button.backToLogin'), {
            defaultValue: t(
              getTranslationKey(
                FALLBACK_TRANSLATION_PREFIX,
                'button.backToLogin',
              ),
            ),
          })}
        </Button>
      </Form>
    </Page>
  );
}

export default FailedPage;
